import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { TestTube, CheckCircle, XCircle, AlertCircle, BarChart3, Database, Send } from "lucide-react";

const AnalyticsTest = () => {
  const [testResults, setTestResults] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);
  const { toast } = useToast();

  const testAnalyticsFunction = async () => {
    setIsTesting(true);
    setTestResults(null);
    
    try {
      console.log('Testing analytics function...');
      
      // Test 1: GET request to check analytics function
      const { data: getResponse, error: getError } = await supabase.functions.invoke('site_analytics_20251219_1900', {
        method: 'GET',
        body: { period: '7' }
      });
      
      if (getError) {
        throw new Error(`Analytics GET failed: ${getError.message}`);
      }
      
      console.log('Analytics GET test successful:', getResponse);
      
      setTestResults(prev => ({
        ...prev,
        getTest: { success: true, data: getResponse }
      }));
      
    } catch (error: any) {
      console.error('Analytics function test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        getTest: { success: false, error: error.message }
      }));
    } finally {
      setIsTesting(false);
    }
  };

  const testDatabaseAccess = async () => {
    setIsTesting(true);
    
    try {
      console.log('Testing direct database access...');
      
      // Test direct database access
      const { count, error: countError } = await supabase
        .from('site_analytics_20251219_1900')
        .select('*', { count: 'exact', head: true });
      
      if (countError) {
        console.error('Database count error:', countError);
        throw countError;
      }
      
      console.log('Database count:', count);
      
      // Get recent records
      const { data: records, error: recordsError } = await supabase
        .from('site_analytics_20251219_1900')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (recordsError) {
        console.error('Database records error:', recordsError);
        throw recordsError;
      }
      
      console.log('Recent records:', records);
      
      setTestResults(prev => ({
        ...prev,
        dbTest: { 
          success: true, 
          count: count || 0,
          records: records || []
        }
      }));
      
      toast({
        title: "Base de données OK !",
        description: `${count || 0} enregistrements trouvés`,
      });
      
    } catch (error: any) {
      console.error('Database test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        dbTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur base de données",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const insertTestAnalytics = async () => {
    setIsTesting(true);
    
    try {
      console.log('Inserting test analytics data...');
      
      const testData = {
        sessionId: `test_session_${Date.now()}`,
        pageUrl: window.location.href,
        pageTitle: 'Test Analytics Page',
        referrer: document.referrer || null,
        visitDuration: 30
      };
      
      const { data, error } = await supabase.functions.invoke('site_analytics_20251219_1900', {
        body: testData
      });
      
      if (error) {
        console.error('Analytics insert error:', error);
        throw error;
      }
      
      console.log('Analytics insert successful:', data);
      
      setTestResults(prev => ({
        ...prev,
        insertTest: { success: true, data }
      }));
      
      toast({
        title: "Données de test insérées !",
        description: "Vérifiez maintenant la page analytics",
      });
      
    } catch (error: any) {
      console.error('Analytics insert test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        insertTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur d'insertion",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const insertDirectTestData = async () => {
    setIsTesting(true);
    
    try {
      console.log('Inserting direct test data...');
      
      const testRecord = {
        session_id: `direct_test_${Date.now()}`,
        ip_address: 'test-ip',
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        page_title: 'Test Direct Insert',
        referrer: document.referrer || null,
        device_type: 'desktop',
        browser: 'Chrome',
        os: 'Windows',
        visit_duration: 45,
        is_unique_visitor: true,
        is_returning_visitor: false
      };
      
      const { data, error } = await supabase
        .from('site_analytics_20251219_1900')
        .insert([testRecord])
        .select()
        .single();
      
      if (error) {
        console.error('Direct insert error:', error);
        throw error;
      }
      
      console.log('Direct insert successful:', data);
      
      setTestResults(prev => ({
        ...prev,
        directTest: { success: true, data }
      }));
      
      toast({
        title: "Insertion directe réussie !",
        description: `ID: ${data.id}`,
      });
      
    } catch (error: any) {
      console.error('Direct insert test error:', error);
      
      setTestResults(prev => ({
        ...prev,
        directTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur d'insertion directe",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Test Analytics</h1>
            <p className="text-muted-foreground">
              Diagnostiquez pourquoi il n'y a pas de données d'analytics
            </p>
          </div>

          {/* Test Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Tests de diagnostic
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  onClick={testAnalyticsFunction}
                  disabled={isTesting}
                  variant="outline"
                  className="w-full"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {isTesting ? 'Test...' : 'Test Edge Function'}
                </Button>
                
                <Button 
                  onClick={testDatabaseAccess}
                  disabled={isTesting}
                  variant="outline"
                  className="w-full"
                >
                  <Database className="h-4 w-4 mr-2" />
                  {isTesting ? 'Test...' : 'Test Base de données'}
                </Button>
                
                <Button 
                  onClick={insertTestAnalytics}
                  disabled={isTesting}
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isTesting ? 'Insertion...' : 'Insérer via Edge Function'}
                </Button>
                
                <Button 
                  onClick={insertDirectTestData}
                  disabled={isTesting}
                  variant="secondary"
                  className="w-full"
                >
                  <Database className="h-4 w-4 mr-2" />
                  {isTesting ? 'Insertion...' : 'Insertion directe BDD'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Results */}
          {testResults && (
            <Card>
              <CardHeader>
                <CardTitle>Résultats des tests</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {testResults.getTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Edge Function (GET)</span>
                    {testResults.getTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.dbTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Accès base de données</span>
                    {testResults.dbTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {testResults.dbTest.count} enregistrements
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.insertTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Insertion via Edge Function</span>
                    {testResults.insertTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Inséré
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Échec
                      </Badge>
                    )}
                  </div>
                )}
                
                {testResults.directTest && (
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Insertion directe BDD</span>
                    {testResults.directTest.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Inséré
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Échec
                      </Badge>
                    )}
                  </div>
                )}

                {/* Show database records if available */}
                {testResults.dbTest?.success && testResults.dbTest.records?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2">Enregistrements récents :</h4>
                    <div className="space-y-2">
                      {testResults.dbTest.records.map((record: any) => (
                        <div key={record.id} className="p-2 bg-muted rounded text-sm">
                          <div><strong>Page:</strong> {record.page_title}</div>
                          <div><strong>Session:</strong> {record.session_id}</div>
                          <div><strong>Date:</strong> {new Date(record.created_at).toLocaleString('fr-FR')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show error details */}
                {Object.values(testResults).some((result: any) => !result.success) && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">Erreurs détectées</h4>
                        {Object.entries(testResults).map(([key, result]: [string, any]) => (
                          !result.success && (
                            <p key={key} className="text-sm text-red-700 mt-1">
                              <strong>{key}:</strong> {result.error}
                            </p>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions de diagnostic</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Étapes de diagnostic :</h4>
                  <ol className="text-sm text-muted-foreground mt-1 space-y-1 list-decimal list-inside">
                    <li>Testez l'Edge Function pour voir si elle répond</li>
                    <li>Vérifiez l'accès à la base de données</li>
                    <li>Insérez des données de test via Edge Function</li>
                    <li>Insérez des données directement en base</li>
                    <li>Allez sur /analytics pour voir si les données apparaissent</li>
                  </ol>
                </div>
                
                <div>
                  <h4 className="font-medium">Si les tests échouent :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Vérifiez la console (F12) pour les erreurs</li>
                    <li>• Consultez les logs Supabase</li>
                    <li>• Vérifiez les politiques RLS</li>
                    <li>• Vérifiez que la table existe</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Après les tests :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Allez sur <a href="/#/analytics" className="text-blue-600 hover:underline">/analytics</a> pour voir les stats</li>
                    <li>• Naviguez sur le site pour générer plus de données</li>
                    <li>• Attendez quelques minutes pour la synchronisation</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsTest;